/**
 * Mood / wellness & user-doc preferences — daily money mood check-ins, mood
 * history, the daily wellness doc, and preferences/state stored on the
 * `userContent/{uid}` root doc (paywall views, app version, AI features,
 * reflect sort order).
 *
 * Internal module — consumers import from the `userContentService` facade.
 */
import {
    collection,
    doc,
    getDoc,
    getDocs,
    onSnapshot,
    query,
    serverTimestamp,
    setDoc,
    increment,
    deleteField,
    deleteDoc,
    Unsubscribe,
    orderBy
} from 'firebase/firestore';
import { db } from '../firebase';
import { recordActivity } from '../activityDayService';
import { getLocalDateString } from '../../utils/dateUtils';
import {
    MoneyMood,
    DailyWellnessEntry,
    MoodCheckIn,
    TransactionSortOrder,
    ReflectViewMode,
} from '../../types';
import {
    USER_CONTENT_COLLECTION,
    DAILY_WELLNESS_SUBCOLLECTION,
    MOOD_HISTORY_SUBCOLLECTION,
    toIsoString,
} from './shared';

export const getUserDailyWellnessRef = (uid: string) => {
    return doc(db, USER_CONTENT_COLLECTION, uid, DAILY_WELLNESS_SUBCOLLECTION, 'current');
};

/** Save daily money mood check-in (updates current + adds to history) */
export const saveDailyMoneyMood = async (uid: string, mood: MoneyMood, note?: string): Promise<void> => {
    const wellnessRef = getUserDailyWellnessRef(uid);
    const now = new Date().toISOString();
    const todayLocal = getLocalDateString();

    // Update the current daily wellness doc
    const wellnessData: Record<string, any> = {
        moneyMood: mood,
        moneyMoodTimestamp: now,
        date: todayLocal,
        updatedAt: serverTimestamp()
    };
    if (note !== undefined) {
        wellnessData.moneyMoodNote = note;
    }
    await setDoc(wellnessRef, wellnessData, { merge: true });

    // Also save to mood history for long-term tracking (use date as doc ID)
    const historyRef = doc(db, USER_CONTENT_COLLECTION, uid, MOOD_HISTORY_SUBCOLLECTION, todayLocal);
    const historyData: Record<string, any> = {
        date: todayLocal,
        mood,
        createdAt: now
    };
    if (note !== undefined) {
        historyData.note = note;
    }
    await setDoc(historyRef, historyData, { merge: true });

    // Fire-and-forget: record activity day for goal tracking
    recordActivity(uid, 'moodCheckin').catch(() => {});
};

/** Get mood history for a user (most recent first) */
export const getMoodHistory = async (uid: string, limit = 30): Promise<MoodCheckIn[]> => {
    const historyRef = collection(db, USER_CONTENT_COLLECTION, uid, MOOD_HISTORY_SUBCOLLECTION);
    const q = query(historyRef, orderBy('date', 'desc'));
    const snapshot = await getDocs(q);

    const entries: MoodCheckIn[] = snapshot.docs.slice(0, limit).map(docSnap => {
        const data = docSnap.data();
        return {
            id: docSnap.id,
            date: data.date,
            mood: data.mood,
            note: data.note,
            createdAt: toIsoString(data.createdAt) || ''
        };
    });

    return entries;
};

/** Subscribe to mood history */
export const subscribeToMoodHistory = (
    uid: string,
    callback: (history: MoodCheckIn[]) => void,
    limit = 30
): Unsubscribe => {
    const historyRef = collection(db, USER_CONTENT_COLLECTION, uid, MOOD_HISTORY_SUBCOLLECTION);
    const q = query(historyRef, orderBy('date', 'desc'));

    return onSnapshot(q, (snapshot) => {
        const entries: MoodCheckIn[] = snapshot.docs.slice(0, limit).map(docSnap => {
            const data = docSnap.data();
            return {
                id: docSnap.id,
                date: data.date,
                mood: data.mood,
                note: data.note,
                createdAt: toIsoString(data.createdAt) || ''
            };
        });
        callback(entries);
    }, (error) => {
        console.error('Error subscribing to mood history:', error);
        callback([]);
    });
};

/** Subscribe to daily wellness (including mood) */
export const subscribeToDailyWellness = (
    uid: string,
    callback: (wellness: DailyWellnessEntry | null) => void
): Unsubscribe => {
    const wellnessRef = getUserDailyWellnessRef(uid);
    return onSnapshot(wellnessRef, (snapshot) => {
        if (!snapshot.exists()) {
            callback(null);
            return;
        }
        const data = snapshot.data();
        callback({
            id: snapshot.id,
            message: data.message || '',
            date: data.date || '',
            wellnessScore: data.wellnessScore,
            moneyMood: data.moneyMood,
            moneyMoodNote: data.moneyMoodNote,
            moneyMoodTimestamp: toIsoString(data.moneyMoodTimestamp),
            createdAt: toIsoString(data.createdAt) || '',
            updatedAt: toIsoString(data.updatedAt) || ''
        });
    }, (error) => {
        console.error('Error subscribing to daily wellness:', error);
        callback(null);
    });
};

/**
 * Record that the membership paywall was shown to this user. Increments a
 * lifetime impression counter plus a per-source breakdown so we can separate
 * "never saw the paywall" (exposure problem) from "saw it N times and didn't
 * convert" (offer/pricing problem). Fire-and-forget on the paywall-open path.
 *
 * NOTE: counters live in a nested map (`paywallViews.count`, not a dotted key)
 * because `setDoc(merge)` treats dotted keys as LITERAL field names — see the
 * same gotcha in cartCompanionService. `source` is sanitized to a safe Firestore
 * map key.
 */
export const recordPaywallView = async (uid: string, source = 'unknown'): Promise<void> => {
    const safeSource = (source || 'unknown').replace(/[.~/[\]*]/g, '_').slice(0, 64);
    const userContentRef = doc(db, USER_CONTENT_COLLECTION, uid);
    await setDoc(userContentRef, {
        paywallViews: {
            count: increment(1),
            lastViewedAt: serverTimestamp(),
            bySource: { [safeSource]: increment(1) },
        },
        updatedAt: serverTimestamp(),
    }, { merge: true });
};

/**
 * Persist the app version the account was created on. Write-once and immutable:
 * if `signupAppVersion` already exists it is never overwritten (a later login
 * must not clobber the original signup version). Also seeds `lastSeenAppVersion`
 * so a brand-new account has a current version immediately, without waiting for
 * the next app launch. Forward-looking analytics only — historical accounts are
 * intentionally not backfilled (their signup version is unknowable).
 */
export const setSignupAppVersion = async (uid: string, appVersion: string): Promise<void> => {
    const userContentRef = doc(db, USER_CONTENT_COLLECTION, uid);
    const snap = await getDoc(userContentRef);
    if (snap.exists() && snap.data()?.signupAppVersion) return; // immutable — already recorded
    await setDoc(userContentRef, {
        signupAppVersion: appVersion,
        lastSeenAppVersion: appVersion,
        updatedAt: serverTimestamp(),
    }, { merge: true });
};

/**
 * Upsert the version/platform of the current authenticated session so active
 * users can be segmented by the build they are running. Cheap flat merge with
 * no read — safe to call on the hot login path.
 *
 * Three fields, and the distinctions between them matter:
 *
 * - `lastSeenAppVersion` — the JS BUNDLE version. A Capgo OTA moves this
 *   without replacing the native binary, so it can overstate what the device
 *   can actually do. Kept for continuity; do NOT make native decisions on it.
 * - `lastSeenNativeVersion` — the NATIVE shell version (Info.plist /
 *   build.gradle). OTA cannot forge it. This is the field the update-gate
 *   adoption tracker reads, and the one the `force_update_below` decision is
 *   made on. Null on web.
 * - `lastSeenAt` — a login timestamp written ONLY here. It exists because
 *   `updatedAt` on this doc is touched by ~8 other writers (sequences, videos,
 *   wellness…), so bucketing on `updatedAt` would measure "last time anything
 *   wrote to this user" rather than "last time this user opened the app".
 */
export const setLastSeenAppVersion = async (
    uid: string,
    appVersion: string,
    session?: { platform?: string; nativeVersion?: string | null },
): Promise<void> => {
    const userContentRef = doc(db, USER_CONTENT_COLLECTION, uid);
    await setDoc(userContentRef, {
        lastSeenAppVersion: appVersion,
        ...(session?.platform ? { lastSeenPlatform: session.platform } : {}),
        ...(session?.nativeVersion ? { lastSeenNativeVersion: session.nativeVersion } : {}),
        lastSeenAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
    }, { merge: true });
};

export const getAiDisclaimerAccepted = async (uid: string): Promise<boolean> => {
    const userContentRef = doc(db, USER_CONTENT_COLLECTION, uid);
    const snap = await getDoc(userContentRef);
    return snap.exists() && snap.data()?.aiDisclaimerAcceptedAt != null;
};

export const setAiDisclaimerAccepted = async (uid: string): Promise<void> => {
    const userContentRef = doc(db, USER_CONTENT_COLLECTION, uid);
    await setDoc(userContentRef, { aiDisclaimerAcceptedAt: serverTimestamp(), updatedAt: serverTimestamp() }, { merge: true });
};

/** AI features preference - persisted to userContent/{uid} */
export const subscribeToAiPreference = (
    uid: string,
    callback: (aiEnabled: boolean) => void
): Unsubscribe => {
    const userContentRef = doc(db, USER_CONTENT_COLLECTION, uid);
    return onSnapshot(userContentRef, (snapshot) => {
        // Default to true (AI enabled) if field doesn't exist
        const enabled = snapshot.exists() ? (snapshot.data()?.aiEnabled !== false) : true;
        callback(enabled);
    }, (error) => {
        console.error('Error subscribing to AI preference:', error);
        callback(true);
    });
};

export const setAiPreference = async (uid: string, enabled: boolean): Promise<void> => {
    const userContentRef = doc(db, USER_CONTENT_COLLECTION, uid);
    await setDoc(userContentRef, { aiEnabled: enabled, updatedAt: serverTimestamp() }, { merge: true });
};

/** Clear AI-related data when user disables AI features */
export const clearAiData = async (uid: string): Promise<void> => {
    const userContentRef = doc(db, USER_CONTENT_COLLECTION, uid);
    const narrativeRef = doc(db, USER_CONTENT_COLLECTION, uid, 'debtPayoff', 'narrative');
    await Promise.all([
        setDoc(userContentRef, {
            aiDisclaimerAcceptedAt: deleteField(),
            updatedAt: serverTimestamp(),
        }, { merge: true }),
        deleteDoc(narrativeRef).catch(() => undefined),
    ]);
};

// ── User Preferences ──────────────────────────────────────────────

/** Get the user's saved reflect sort order (returns undefined if not set). */
export const getReflectSortOrder = async (uid: string): Promise<TransactionSortOrder | undefined> => {
    const snap = await getDoc(doc(db, USER_CONTENT_COLLECTION, uid));
    return snap.exists() ? snap.data().reflectSortOrder : undefined;
};

/** Save the user's reflect sort order preference. */
export const saveReflectSortOrder = async (uid: string, sortOrder: TransactionSortOrder): Promise<void> => {
    await setDoc(doc(db, USER_CONTENT_COLLECTION, uid), { reflectSortOrder: sortOrder }, { merge: true });
};

/** Get the user's saved reflect view mode — list or stack (returns undefined if not set). */
export const getReflectViewMode = async (uid: string): Promise<ReflectViewMode | undefined> => {
    const snap = await getDoc(doc(db, USER_CONTENT_COLLECTION, uid));
    return snap.exists() ? snap.data().reflectViewMode : undefined;
};

/** Save the user's reflect view mode preference. */
export const saveReflectViewMode = async (uid: string, mode: ReflectViewMode): Promise<void> => {
    await setDoc(doc(db, USER_CONTENT_COLLECTION, uid), { reflectViewMode: mode }, { merge: true });
};
